import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Button, Input, FormGroup, Label, Table, Alert } from 'reactstrap';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import 'react-day-picker/lib/style.css';
import FileServer from 'file-saver';
import { axiosApi } from '../Basicos/axiosInstances';
import { dateToPtBr, dateAndHourLaravel, formatoData, formatDate, parseDate, pt_br } from '../Basicos/funcoes';
import { FiEye, FiDownload, FiTrash2 } from 'react-icons/fi';
import { ROTULOS_SITUACAO, CORES_SITUACAO } from './vacinacaoConstantes';

const ROTULOS_MOTIVO_RECUSA = {
    nao_e_pdf_oficial: 'Não é o PDF emitido pelo sistema (parece foto)',
    tipo_incompativel: 'Tipo escolhido não corresponde ao conteúdo',
    protegido_por_senha: 'PDF protegido por senha',
    nenhuma_vacina_encontrada: 'Nenhuma vacina exigida encontrada',
    arquivo_ilegivel: 'Arquivo ilegível',
    nao_e_comprovante_vacinacao: 'Não é um comprovante de vacinação',
    documento_de_outra_pessoa: 'Documento de outra pessoa',
    outro: 'Outro motivo',
};

const OPCOES_MOTIVO_RECUSA_MANUAL = [
    { valor: 'arquivo_ilegivel', rotulo: 'Arquivo ilegível' },
    { valor: 'nao_e_comprovante_vacinacao', rotulo: 'Não é comprovante de vacinação' },
    { valor: 'documento_de_outra_pessoa', rotulo: 'Documento de outra pessoa' },
    { valor: 'outro', rotulo: 'Outro' },
];

function statusExibicaoDocumento(documento) {
    if (documento.status_leitura === 'recusado') return 'recusado';
    if (documento.revisao_solicitada_em) return 'pendente';
    return 'aceito';
}

function converterParaDataObj(val) {
    if (!val || val === '—') return undefined;
    if (typeof val === 'string') {
        const clean = val.split('T')[0].split(' ')[0];
        const parsed = parseDate(clean, 'yyyy-MM-dd') || parseDate(val, formatoData);
        if (parsed) return parsed;
    }
    return parseDate(val, formatoData);
}

function formatarDataPtBr(dataRaw) {
    if (!dataRaw || dataRaw === '—') return '—';
    const str = String(dataRaw).trim();
    if (!str) return '—';

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        return str;
    }

    const dateOnly = str.split('T')[0].split(' ')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
        const [year, month, day] = dateOnly.split('-');
        return `${day}/${month}/${year}`;
    }

    try {
        const parsed = new Date(str);
        if (!isNaN(parsed.getTime())) {
            const day = String(parsed.getDate()).padStart(2, '0');
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const year = parsed.getFullYear();
            return `${day}/${month}/${year}`;
        }
    } catch (e) {
        // ignore
    }

    return str;
}

export default function AnaliseVacinacao({ campanhaId, alunoId, onVoltar }) {
    const [carregando, setCarregando] = useState(true);
    const [dados, setDados] = useState(null);
    const [gruposVacinas, setGruposVacinas] = useState([]);
    const [motivo, setMotivo] = useState('');
    const [documentoSelecionado, setDocumentoSelecionado] = useState(null);
    const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
    const [modalConformeAberto, setModalConformeAberto] = useState(false);
    const [modalNaoConformeAberto, setModalNaoConformeAberto] = useState(false);
    const [modalEnviarDocumentoAberto, setModalEnviarDocumentoAberto] = useState(false);
    const [tipoDocumentoId, setTipoDocumentoId] = useState('');
    const [arquivo, setArquivo] = useState(null);
    const [enviandoDocumento, setEnviandoDocumento] = useState(false);
    const [erroUpload, setErroUpload] = useState('');
    const [erroValidacao, setErroValidacao] = useState('');
    const [sucessoSalvar, setSucessoSalvar] = useState('');
    const [salvandoAjustes, setSalvandoAjustes] = useState(false);
    const [vacinasLidasUpload, setVacinasLidasUpload] = useState([]);
    const [modalVacinasLidasAberta, setModalVacinasLidasAberta] = useState(false);
    const [processandoDocumentoId, setProcessandoDocumentoId] = useState(null);
    const [documentoParaRecusar, setDocumentoParaRecusar] = useState(null);
    const [motivoRecusaDocumento, setMotivoRecusaDocumento] = useState('');
    const [detalhesRecusaDocumento, setDetalhesRecusaDocumento] = useState('');
    const [erroRecusaDocumento, setErroRecusaDocumento] = useState('');
    const [documentoParaRemover, setDocumentoParaRemover] = useState(null);
    const [erroRemoverDocumento, setErroRemoverDocumento] = useState('');
    const inputArquivoRef = useRef(null);

    const formatarTamanhoArquivo = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(1).replace('.', ',')} MB`;
    };

    const carregarDados = async () => {
        if (!campanhaId || !alunoId) {
            return;
        }

        setCarregando(true);
        try {
            const { data } = await axiosApi.get(`/vacinacao/analise/${campanhaId}/${alunoId}`);
            setDados(data);

            const agrupadasPorVacina = {};
            (data.vacinas_do_aluno || []).forEach((v) => {
                if (!v.vacina_id) return;
                if (!agrupadasPorVacina[v.vacina_id]) {
                    agrupadasPorVacina[v.vacina_id] = [];
                }
                agrupadasPorVacina[v.vacina_id].push(v);
            });

            const grupos = (data.vacinas_exigidas || []).map((exigida) => {
                const registrosAluno = (agrupadasPorVacina[exigida.vacina_id] || [])
                    .slice()
                    .sort((a, b) => {
                        if (!a.data_aplicacao) return 1;
                        if (!b.data_aplicacao) return -1;
                        return a.data_aplicacao.localeCompare(b.data_aplicacao);
                    });

                const totalExigido = Number(exigida.doses_necessarias) || 1;

                const doses = Array.from({ length: totalExigido }, (_, index) => {
                    const registro = registrosAluno[index];
                    return {
                        numeroDose: `${index + 1}º`,
                        data_aplicacao: registro?.data_aplicacao || '',
                        documento_id: registro?.documento_vacinacao_aluno_id ? String(registro.documento_vacinacao_aluno_id) : '',
                        situacao: registro ? 'atende' : 'nao_encontrada',
                    };
                });

                return {
                    tipo_vacina_exigida_id: exigida.id,
                    vacina_id: exigida.vacina_id,
                    nome: exigida.nome,
                    doses_necessarias: totalExigido,
                    doses,
                };
            });

            setGruposVacinas(grupos);

            const primeiroAceito = (data.documentos || []).find((documento) => documento.status_leitura === 'aceito');
            setDocumentoSelecionado(primeiroAceito || null);
        } catch (error) {
            console.log(error);
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        carregarDados();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campanhaId, alunoId]);

    useEffect(() => {
        if (sucessoSalvar) {
            const timer = setTimeout(() => {
                setSucessoSalvar('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [sucessoSalvar]);

    // documento.arquivo_url de um arquivo enviado pelo aluno aponta pro unichristus-backend, que manda
    // X-Frame-Options: sameorigin — o navegador recusa embutir isso num iframe de outra origem. Busca
    // como blob (mesma origem da tela) em vez de usar a URL direta no iframe, e some com o anterior
    // pra não vazar memória trocando de documento.
    useEffect(() => {
        let cancelado = false;
        let urlCriada = null;

        if (documentoSelecionado?.status_leitura === 'aceito') {
            axiosApi.get(`/vacinacao/documento/${documentoSelecionado.id}/visualizar`, { responseType: 'blob' })
                .then(({ data }) => {
                    if (cancelado) return;
                    urlCriada = window.URL.createObjectURL(data);
                    setPreviewBlobUrl(urlCriada);
                })
                .catch(() => {
                    if (!cancelado) setPreviewBlobUrl(null);
                });
        } else {
            setPreviewBlobUrl(null);
        }

        return () => {
            cancelado = true;
            if (urlCriada) window.URL.revokeObjectURL(urlCriada);
        };
    }, [documentoSelecionado]);

    const enviarDocumento = async () => {
        if (!tipoDocumentoId || !arquivo) {
            return;
        }

        setEnviandoDocumento(true);
        setErroUpload('');

        const formData = new FormData();
        formData.append('tipo_documento_vacinacao_id', tipoDocumentoId);
        formData.append('arquivo', arquivo);

        try {
            const { data } = await axiosApi.post(`/vacinacao/analise/${campanhaId}/${alunoId}/documento`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setTipoDocumentoId('');
            setArquivo(null);
            setModalEnviarDocumentoAberto(false);

            const lidas = data.vacinas_lidas || data.documento?.vacinas_identificadas || [];
            setVacinasLidasUpload(lidas);
            setModalVacinasLidasAberta(true);

            await carregarDados();
        } catch (error) {
            setErroUpload(error.response?.data?.erro || 'Não foi possível enviar o documento.');
        } finally {
            setEnviandoDocumento(false);
        }
    };

    const fecharModalEnviarDocumento = () => {
        setModalEnviarDocumentoAberto(false);
        setTipoDocumentoId('');
        setArquivo(null);
        setErroUpload('');
    };

    // documento.arquivo_url (storage) fica fora do CORS (config/cors.php só libera api/*) — por isso
    // baixa pela rota da API (vacinacao/documento/{id}/download), que já é coberta pelo CORS e carrega
    // o token de autenticação do axiosApi normalmente.
    const baixarDocumento = async (documento) => {
        try {
            const { data } = await axiosApi.get(`/vacinacao/documento/${documento.id}/download`, { responseType: 'blob' });
            FileServer.saveAs(new File([data], documento.nome, { type: data.type }));
        } catch (error) {
            window.open(documento.arquivo_url, '_blank', 'noopener,noreferrer');
        }
    };

    const aceitarDocumentoPendente = async (documento) => {
        setProcessandoDocumentoId(documento.id);
        try {
            await axiosApi.put(`/vacinacao/documento/${documento.id}/aceitar`);
            await carregarDados();
        } catch (error) {
            // erro raro (documento já resolvido por outra aba, etc.) — só recarrega pra refletir o estado real
            await carregarDados();
        } finally {
            setProcessandoDocumentoId(null);
        }
    };

    const abrirModalRecusarDocumento = (documento) => {
        setDocumentoParaRecusar(documento);
        setMotivoRecusaDocumento('');
        setDetalhesRecusaDocumento('');
        setErroRecusaDocumento('');
    };

    const confirmarRecusarDocumento = async () => {
        if (!motivoRecusaDocumento) {
            setErroRecusaDocumento('Escolha um motivo da recusa.');
            return;
        }

        setProcessandoDocumentoId(documentoParaRecusar.id);
        try {
            await axiosApi.put(`/vacinacao/documento/${documentoParaRecusar.id}/recusar`, {
                motivo_recusa: motivoRecusaDocumento,
                detalhes_recusa: detalhesRecusaDocumento || null,
            });
            setDocumentoParaRecusar(null);
            await carregarDados();
        } catch (error) {
            setErroRecusaDocumento(error.response?.data?.erro || 'Não foi possível recusar o documento.');
        } finally {
            setProcessandoDocumentoId(null);
        }
    };

    // Corrige um envio errado feito pela própria Secretaria (ex.: escolheu o tipo errado) — só
    // aparece pra documentos que ela mesma enviou. Documento do aluno (pelo app) só ele remove.
    const confirmarRemoverDocumento = async () => {
        setProcessandoDocumentoId(documentoParaRemover.id);
        setErroRemoverDocumento('');
        try {
            await axiosApi.delete(`/vacinacao/documento/${documentoParaRemover.id}`);
            setDocumentoParaRemover(null);
            await carregarDados();
        } catch (error) {
            setErroRemoverDocumento(error.response?.data?.erro || 'Não foi possível remover o documento.');
        } finally {
            setProcessandoDocumentoId(null);
        }
    };

    const atualizarDose = (grupoIndex, doseIndex, campo, valor) => {
        setGruposVacinas((atual) =>
            atual.map((grupo, gIdx) => {
                if (gIdx !== grupoIndex) return grupo;
                const novasDoses = grupo.doses.map((dose, dIdx) => {
                    if (dIdx !== doseIndex) return dose;
                    return { ...dose, [campo]: valor };
                });
                return { ...grupo, doses: novasDoses };
            })
        );
    };

    const validarDoses = () => {
        setErroValidacao('');
        for (const grupo of gruposVacinas) {
            for (const dose of grupo.doses) {
                if (dose.situacao === 'atende') {
                    const rawDate = (dose.data_aplicacao || '').trim();
                    if (!rawDate || rawDate === '—') {
                        setErroValidacao(`Para marcar a dose ${dose.numeroDose} da vacina "${grupo.nome}" como "Atende", é obrigatório preencher a Data Dose.`);
                        return false;
                    }
                }
            }
        }
        return true;
    };

    const todasDosesCumpridas = () => {
        if (!gruposVacinas.length) return false;
        return gruposVacinas.every((grupo) =>
            grupo.doses.every((dose) => {
                if (dose.situacao !== 'atende') return false;
                const rawDate = (dose.data_aplicacao || '').trim();
                return rawDate && rawDate !== '—';
            })
        );
    };

    // Pré-preenche o campo de observações do modal "Não conforme" a partir do que já está marcado
    // como "Não encontrada" na tabela — a Secretaria ainda pode editar antes de enviar.
    const gerarObservacoesAutomaticas = () => {
        const partes = gruposVacinas
            .map((grupo) => {
                const indicesFaltantes = grupo.doses
                    .map((dose, index) => (dose.situacao !== 'atende' ? index + 1 : null))
                    .filter((indice) => indice !== null);

                if (!indicesFaltantes.length) return null;

                if (indicesFaltantes.length === 1) {
                    return `${indicesFaltantes[0]}ª dose de ${grupo.nome}`;
                }

                return `${indicesFaltantes.length} doses de ${grupo.nome}`;
            })
            .filter(Boolean);

        if (!partes.length) return '';

        const lista = partes.length === 1
            ? partes[0]
            : `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;

        return `Não encontramos: ${lista}. Envie um novo comprovante.`;
    };

    const montarAjustes = () => {
        return gruposVacinas.map((grupo) => {
            const listaDosesAtendidas = [];
            let ultimaDose = null;
            let docId = null;

            grupo.doses.forEach((d, index) => {
                if (d.situacao === 'atende') {
                    const rawDate = d.data_aplicacao;
                    let isoDate = null;
                    if (rawDate && rawDate !== '—') {
                        isoDate = rawDate;
                        if (/^\d{2}\/\d{2}\/\d{4}$/.test(rawDate)) {
                            const [day, month, year] = rawDate.split('/');
                            isoDate = `${year}-${month}-${day}`;
                        } else if (rawDate.includes('T') || rawDate.includes(' ')) {
                            isoDate = rawDate.split('T')[0].split(' ')[0];
                        }
                    }

                    if (isoDate) {
                        const documentoId = d.documento_id ? Number(d.documento_id) : null;
                        listaDosesAtendidas.push({
                            dose: index + 1,
                            data_aplicacao: isoDate,
                            documento_id: documentoId,
                        });

                        if (!ultimaDose || isoDate > ultimaDose) {
                            ultimaDose = isoDate;
                        }
                        if (!docId && documentoId) {
                            docId = documentoId;
                        }
                    }
                }
            });

            return {
                tipo_vacina_exigida_id: grupo.tipo_vacina_exigida_id,
                doses_identificadas: listaDosesAtendidas.length,
                ultima_dose: ultimaDose,
                documento_id: docId,
                doses: listaDosesAtendidas,
            };
        });
    };

    const enviarRevisao = async (conformeParam, motivoParam) => {
        setSalvandoAjustes(true);
        setErroValidacao('');
        setSucessoSalvar('');

        try {
            await axiosApi.put(`/vacinacao/analise/${campanhaId}/${alunoId}/revisar`, {
                conforme: conformeParam,
                motivo: conformeParam ? null : motivoParam,
                ajustes: montarAjustes(),
            });
            return true;
        } catch (error) {
            console.log(error);
            setErroValidacao('Ocorreu um erro ao salvar as alterações.');
            return false;
        } finally {
            setSalvandoAjustes(false);
        }
    };

    // Botão "Salvar": só grava a tabela, sem emitir parecer nem notificar o aluno. O servidor sempre
    // recalcula a situação sozinho (incompleto/pendente_revisao_manual/conforme_automatico) — mesmo
    // que o salvamento feche todas as pendências, quem decide fechar o caso é sempre o clique
    // explícito em "Conforme".
    const salvarAlteracoes = async () => {
        if (!validarDoses()) return;

        const sucesso = await enviarRevisao(true, null);
        if (sucesso) {
            setSucessoSalvar('Alterações salvas com sucesso!');
            await carregarDados();
        }
    };

    const abrirModalConforme = () => {
        if (!validarDoses() || !todasDosesCumpridas()) return;
        setModalConformeAberto(true);
    };

    const confirmarConforme = async () => {
        const sucesso = await enviarRevisao(true, null);
        if (sucesso) {
            setModalConformeAberto(false);
            onVoltar(true);
        }
    };

    const abrirModalNaoConforme = () => {
        if (!validarDoses()) return;
        setMotivo(gerarObservacoesAutomaticas());
        setModalNaoConformeAberto(true);
    };

    const confirmarNaoConforme = async () => {
        const sucesso = await enviarRevisao(false, motivo);
        if (sucesso) {
            setModalNaoConformeAberto(false);
            onVoltar(true);
        }
    };

    if (carregando || !dados) {
        return (
            <Col sm={12} md={12} className="col-conteudo p-4">
                <p>Carregando...</p>
            </Col>
        );
    }

    // null = aluno sem nenhum histórico ainda (avaliarConformidade() não cria registro nesse
    // caso) — "sem_envio" é o default correto, não "pendente_revisao_manual" (que implicaria
    // alguém já estar com o caso em mãos, o que não é verdade pra quem não mandou nada).
    const situacaoAtual = dados.situacao_atual?.situacao || 'sem_envio';
    const editavel = !['conforme_automatico', 'conforme_revisado'].includes(situacaoAtual);

    return (
        <Col sm={12} md={12} className="col-conteudo p-4">
            <div className="mb-2">
                <a href="#voltar" onClick={(e) => { e.preventDefault(); onVoltar(false); }}>Vacinação - Análise do aluno</a>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 style={{ color: 'var(--cor-principal)', fontWeight: 700 }}>Análise da Vacinação</h4>
                <div className="d-flex align-items-center" style={{ gap: '.5rem' }}>
                    <Button color="primary" outline onClick={() => onVoltar(false)}>Voltar</Button>
                    {editavel && (
                        <>
                            <Button color="primary" outline onClick={abrirModalNaoConforme} disabled={salvandoAjustes}>
                                Não conforme
                            </Button>
                            <Button color="primary" outline onClick={salvarAlteracoes} disabled={salvandoAjustes}>
                                {salvandoAjustes ? 'Salvando...' : 'Salvar'}
                            </Button>
                            <Button color="primary" onClick={abrirModalConforme} disabled={salvandoAjustes || !todasDosesCumpridas()}>
                                Conforme
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '.5rem', padding: '1rem' }}>
                <div>
                    <h5 className="mb-1"><b>{dados.aluno?.nome}</b></h5>
                    <span style={{ color: '#6c757d' }}>
                        Matrícula {dados.aluno?.matricula} · {dados.aluno?.curso_descricao} · {dados.aluno?.semestre} semestre · Grupo {dados.aluno?.grupo}
                    </span>
                </div>
                <span
                    className="px-3 py-1"
                    style={{
                        borderRadius: '1rem',
                        fontWeight: 700,
                        color: CORES_SITUACAO[situacaoAtual],
                        backgroundColor: `${CORES_SITUACAO[situacaoAtual]}22`,
                    }}
                >
                    {ROTULOS_SITUACAO[situacaoAtual]}
                </span>
            </div>

            <Row>
                <Col md={5}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0"><b>Documentos enviados</b></h6>
                    </div>

                    {(dados.documentos || []).map((documento) => {
                        const status = statusExibicaoDocumento(documento);
                        const corStatus = {
                            aceito: { cor: '#28a745', fundo: '#28a74522', rotulo: 'Aceito' },
                            pendente: { cor: '#b36b00', fundo: '#fff3cd', rotulo: 'Pendente' },
                            recusado: { cor: '#dc3545', fundo: '#dc354522', rotulo: 'Recusado' },
                        }[status];
                        const processandoEsteDocumento = processandoDocumentoId === documento.id;

                        return (
                            <div
                                key={documento.id}
                                onClick={() => setDocumentoSelecionado(documento)}
                                style={{
                                    border: documentoSelecionado?.id === documento.id ? '2px solid var(--cor-principal)' : '1px solid #e9ecef',
                                    borderRadius: '.75rem',
                                    padding: '.85rem 1rem',
                                    marginBottom: '.75rem',
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ gap: '.5rem', minWidth: 0 }}>
                                        <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{documento.nome}</b>
                                        <span
                                            className="px-2"
                                            style={{
                                                borderRadius: '1rem',
                                                fontSize: '.75rem',
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                color: corStatus.cor,
                                                backgroundColor: corStatus.fundo,
                                            }}
                                        >
                                            {corStatus.rotulo}
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center" style={{ gap: '.6rem', flexShrink: 0, marginLeft: '.5rem' }}>
                                        <FiEye
                                            size={16}
                                            style={{ color: '#6c757d', cursor: 'pointer' }}
                                            title="Abrir PDF em nova aba"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(documento.arquivo_url, '_blank', 'noopener,noreferrer');
                                            }}
                                        />
                                        <FiDownload
                                            size={16}
                                            style={{ color: '#6c757d', cursor: 'pointer' }}
                                            title="Baixar PDF"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                baixarDocumento(documento);
                                            }}
                                        />
                                        {documento.enviado_por_nome && editavel && (
                                            <FiTrash2
                                                size={16}
                                                style={{ color: '#dc3545', cursor: 'pointer' }}
                                                title="Remover documento (enviado pela Secretaria)"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setErroRemoverDocumento('');
                                                    setDocumentoParaRemover(documento);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div style={{ color: '#6c757d', fontSize: '.85rem' }}>{documento.tipo_documento_descricao}</div>
                                <div className="d-flex justify-content-between align-items-center mt-1" style={{ color: '#6c757d', fontSize: '.8rem' }}>
                                    <span>
                                        {documento.enviado_por_nome
                                            ? `Enviado pela Secretaria em ${dateToPtBr(documento.created_at?.slice(0, 10))} · ${documento.enviado_por_nome}`
                                            : `Enviado em ${dateToPtBr(documento.created_at?.slice(0, 10))}`}
                                        {status !== 'recusado' && (
                                            ` · ${documento.vacinas_identificadas_count} vacinas, ${documento.doses_identificadas_count} doses`
                                        )}
                                    </span>
                                    {status !== 'recusado' && (
                                        <span
                                            style={{
                                                fontSize: '.78rem',
                                                fontWeight: 600,
                                                color: '#0024d6',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                marginLeft: '8px',
                                                whiteSpace: 'nowrap',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setVacinasLidasUpload(documento.vacinas_identificadas || []);
                                                setModalVacinasLidasAberta(true);
                                            }}
                                        >
                                            Ver vacinas lidas
                                        </span>
                                    )}
                                </div>
                                {status === 'recusado' && (
                                    <div className="mt-2 p-2" style={{ background: '#fdecea', color: '#dc3545', borderRadius: '.35rem', fontSize: '.85rem' }}>
                                        {documento.recusado_por_nome && (
                                            <div style={{ fontWeight: 600, marginBottom: '.2rem' }}>
                                                Recusado em {dateToPtBr(documento.recusado_em?.slice(0, 10))} por {documento.recusado_por_nome}
                                            </div>
                                        )}
                                        Motivo da recusa: {ROTULOS_MOTIVO_RECUSA[documento.motivo_recusa] || documento.motivo_recusa}
                                        {documento.detalhes_recusa && <div className="mt-1">{documento.detalhes_recusa}</div>}
                                    </div>
                                )}
                                {status === 'pendente' && (
                                    <>
                                        <div className="mt-2 p-2" style={{ background: '#fff3cd', color: '#92400e', borderRadius: '.35rem', fontSize: '.85rem', fontWeight: 600 }}>
                                            Revisão solicitada pelo aluno em {dateAndHourLaravel(documento.revisao_solicitada_em)}
                                        </div>
                                        <div className="d-flex mt-2" style={{ gap: '.5rem' }}>
                                            <Button
                                                outline
                                                color="primary"
                                                size="sm"
                                                disabled={processandoEsteDocumento}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    abrirModalRecusarDocumento(documento);
                                                }}
                                            >
                                                Recusar
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                disabled={processandoEsteDocumento}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    aceitarDocumentoPendente(documento);
                                                }}
                                            >
                                                {processandoEsteDocumento ? 'Aceitando...' : 'Aceitar'}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="d-flex justify-content-center mb-3">
                        <Button
                            outline
                            color="primary"
                            style={{ borderRadius: '2rem', padding: '.65rem 2rem', width: '70%' }}
                            onClick={() => setModalEnviarDocumentoAberto(true)}
                        >
                            + Enviar documento
                        </Button>
                    </div>

                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ border: '1px solid #e9ecef', borderRadius: '.5rem', minHeight: 320, color: '#adb5bd' }}
                    >
                        {documentoSelecionado?.status_leitura === 'aceito' && previewBlobUrl ? (
                            <iframe
                                title="Pré-visualização do PDF"
                                src={previewBlobUrl}
                                style={{ width: '100%', height: 320, border: 'none' }}
                            />
                        ) : documentoSelecionado?.status_leitura === 'aceito' ? 'Carregando pré-visualização...' : 'Pré-visualização do PDF'}
                    </div>
                </Col>

                <Col md={7} className="tabela-vacinacao-datepicker">
                    <style>{`
                        .tabela-vacinacao-datepicker {
                            position: relative;
                        }
                        .tabela-vacinacao-datepicker .DayPickerInput {
                            display: block;
                            position: relative;
                            width: 100%;
                        }
                        .tabela-vacinacao-datepicker .DayPickerInput-OverlayWrapper {
                            top: 100% !important;
                            left: auto !important;
                            right: 0 !important;
                            position: absolute !important;
                            z-index: 1050 !important;
                        }
                        .tabela-vacinacao-datepicker .DayPickerInput-Overlay {
                            position: absolute !important;
                            right: 0 !important;
                            left: auto !important;
                            margin-top: 4px !important;
                            background: #ffffff !important;
                            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18) !important;
                            border-radius: 8px !important;
                            border: 1px solid #cbd5e1 !important;
                        }
                        .tabela-vacinacao-datepicker .DayPickerInput-OverlayWrapper.abre-para-cima {
                            top: auto !important;
                            bottom: 100% !important;
                        }
                        .tabela-vacinacao-datepicker .DayPickerInput-OverlayWrapper.abre-para-cima .DayPickerInput-Overlay {
                            margin-top: 0 !important;
                            margin-bottom: 4px !important;
                        }
                    `}</style>
                    <h6 className="mb-3"><b>Vacinas exigidas × identificadas</b></h6>
                    {(() => {
                        const totalDoseRows = gruposVacinas.reduce((acc, g) => acc + g.doses.length, 0);

                        return (
                    <Table bordered size="sm" style={{ borderColor: '#0024d6' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#0024d6', color: '#ffffff' }}>
                                <th className="p-2" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6', width: '130px' }}>Vacina</th>
                                <th className="p-2 text-center" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6', width: '50px' }}>Dose</th>
                                <th className="p-2 text-center" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6', width: '125px' }}>Data Dose</th>
                                <th className="p-2 text-center" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6', width: '210px' }}>Documento</th>
                                <th className="p-2 text-center" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6', width: '135px' }}>Situação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gruposVacinas.map((grupo, gIndex) => (
                                <React.Fragment key={grupo.tipo_vacina_exigida_id || gIndex}>
                                    {grupo.doses.map((dose, dIndex) => {
                                        const indiceLinhaGlobal = gruposVacinas.slice(0, gIndex).reduce((acc, g) => acc + g.doses.length, 0) + dIndex;
                                        // Linhas perto do fim da tabela tendem a cair perto (ou fora) do fim da
                                        // viewport — abre o calendário pra cima nelas, senão ele fica cortado e
                                        // não tem como dar scroll até o resto (não existe mais página abaixo dele).
                                        const abrirParaCima = totalDoseRows > 3 && indiceLinhaGlobal >= totalDoseRows - 3;

                                        return (
                                        <tr key={`${grupo.tipo_vacina_exigida_id}_${dIndex}`}>
                                            {dIndex === 0 && (
                                                <td
                                                    rowSpan={grupo.doses.length}
                                                    className="align-middle"
                                                    style={{
                                                        verticalAlign: 'middle',
                                                        fontWeight: 500,
                                                        backgroundColor: '#ffffff',
                                                    }}
                                                >
                                                    {grupo.nome}
                                                </td>
                                            )}
                                            <td
                                                className="text-center align-middle"
                                                style={{
                                                    verticalAlign: 'middle',
                                                    fontWeight: 500,
                                                    backgroundColor: '#f8fafc',
                                                }}
                                            >
                                                {dose.numeroDose}
                                            </td>
                                            <td className="p-1 align-middle text-center" style={{ width: '125px' }}>
                                                <DayPickerInput
                                                    placeholder="dd/mm/aaaa"
                                                    value={converterParaDataObj(dose.data_aplicacao)}
                                                    format={formatoData}
                                                    formatDate={formatDate}
                                                    parseDate={parseDate}
                                                    onDayChange={(day) => {
                                                        if (!editavel) return;
                                                        const strData = day ? formatDate(day, 'yyyy-MM-dd') : '';
                                                        atualizarDose(gIndex, dIndex, 'data_aplicacao', strData);
                                                    }}
                                                    dayPickerProps={{ locale: 'pt-br', ...pt_br }}
                                                    classNames={{
                                                        container: 'DayPickerInput',
                                                        overlayWrapper: abrirParaCima ? 'DayPickerInput-OverlayWrapper abre-para-cima' : 'DayPickerInput-OverlayWrapper',
                                                        overlay: 'DayPickerInput-Overlay',
                                                    }}
                                                    inputProps={{
                                                        className: 'form-control form-control-sm text-center',
                                                        disabled: !editavel,
                                                        style: {
                                                            textAlign: 'center',
                                                            backgroundColor: editavel ? '#ffffff' : '#f1f3f5',
                                                            height: '31px',
                                                            fontSize: '0.85rem',
                                                            borderRadius: '0.25rem',
                                                            border: '1px solid #ced4da',
                                                            width: '100%',
                                                        },
                                                    }}
                                                />
                                            </td>
                                            <td className="p-1 align-middle text-center" style={{ width: '210px' }}>
                                                <Input
                                                    type="select"
                                                    bsSize="sm"
                                                    value={dose.documento_id || ''}
                                                    onChange={(e) => atualizarDose(gIndex, dIndex, 'documento_id', e.target.value)}
                                                    disabled={!editavel}
                                                    style={{ fontSize: '0.8rem', backgroundColor: editavel ? '#ffffff' : '#f1f3f5', textAlign: 'center', paddingLeft: '4px', paddingRight: '4px' }}
                                                >
                                                    <option value="">Selecione...</option>
                                                    {(dados.documentos || []).map((doc) => (
                                                        <option key={doc.id} value={String(doc.id)}>
                                                            {doc.tipo_documento_descricao || doc.nome}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </td>
                                            <td className="p-1 align-middle" style={{ width: '135px' }}>
                                                <Input
                                                    type="select"
                                                    bsSize="sm"
                                                    value={dose.situacao}
                                                    onChange={(e) => atualizarDose(gIndex, dIndex, 'situacao', e.target.value)}
                                                    disabled={!editavel}
                                                    style={{
                                                        color: dose.situacao === 'atende' ? '#16a34a' : '#dc2626',
                                                        fontWeight: 700,
                                                        backgroundColor: editavel ? undefined : '#f1f3f5',
                                                    }}
                                                >
                                                    <option value="atende" style={{ color: '#16a34a', fontWeight: 700 }}>Atende</option>
                                                    <option value="nao_encontrada" style={{ color: '#dc2626', fontWeight: 700 }}>Não encontrada</option>
                                                </Input>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                        );
                    })()}
                </Col>
            </Row>

            {erroValidacao && (
                <Alert color="danger" className="mt-3 mb-0">
                    {erroValidacao}
                </Alert>
            )}
            {sucessoSalvar && (
                <Alert color="success" className="mt-3 mb-0">
                    {sucessoSalvar}
                </Alert>
            )}

            {modalConformeAberto && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050 }}
                >
                    <div style={{ background: '#fff', borderRadius: '.5rem', padding: '1.5rem', maxWidth: 480 }}>
                        <h5 className="mb-3"><b>Confirmar parecer: Conforme</b></h5>
                        <p>
                            Todas as doses exigidas atendem. {dados.aluno?.nome} receberá uma notificação no app e um e-mail informando
                            que a comprovação de vacinação foi concluída.
                        </p>
                        <div className="d-flex justify-content-end">
                            <Button color="primary" outline className="mr-2" onClick={() => setModalConformeAberto(false)} disabled={salvandoAjustes}>
                                Cancelar
                            </Button>
                            <Button color="primary" onClick={confirmarConforme} disabled={salvandoAjustes}>
                                {salvandoAjustes ? 'Confirmando...' : 'Confirmar'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {modalNaoConformeAberto && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050 }}
                >
                    <div style={{ background: '#fff', borderRadius: '.5rem', padding: '1.5rem', maxWidth: 520, width: '90%' }}>
                        <h5 className="mb-3"><b>Confirmar parecer: Não conforme</b></h5>
                        <p>
                            {dados.aluno?.nome} receberá uma notificação no app e um e-mail informando que a comprovação não foi
                            confirmada, com as observações abaixo.
                        </p>
                        <FormGroup>
                            <Label>Observações enviadas ao aluno</Label>
                            <Input type="textarea" rows={4} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                            <small className="text-muted">
                                Preenchido a partir das doses com situação diferente de "Atende". Você pode editar antes de enviar.
                            </small>
                        </FormGroup>
                        <div className="d-flex justify-content-end">
                            <Button color="primary" outline className="mr-2" onClick={() => setModalNaoConformeAberto(false)} disabled={salvandoAjustes}>
                                Cancelar
                            </Button>
                            <Button color="primary" onClick={confirmarNaoConforme} disabled={!motivo || salvandoAjustes}>
                                {salvandoAjustes ? 'Confirmando...' : 'Confirmar'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {modalVacinasLidasAberta && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.5)', zIndex: 1060 }}
                >
                    <div style={{ background: '#fff', borderRadius: '.5rem', padding: '1.5rem', maxWidth: 540, width: '90%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
                        <h5 className="text-center mb-2"><b>Vacinas identificadas no documento</b></h5>
                        <p className="text-muted text-center mb-3" style={{ fontSize: '0.85rem' }}>
                            O documento foi processado com sucesso. Confira abaixo as vacinas e doses identificadas:
                        </p>

                        <div style={{ overflowY: 'auto', flex: 1, maxHeight: '55vh', paddingRight: '4px' }}>
                            {vacinasLidasUpload.length === 0 ? (
                                <div className="p-3 text-center mb-3" style={{ backgroundColor: '#f8fafc', borderRadius: '0.375rem', border: '1px solid #e2e8f0', color: '#64748b' }}>
                                    Nenhuma vacina foi identificada automaticamente neste documento.
                                </div>
                            ) : (
                                <Table bordered size="sm" className="mb-0">
                                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                        <tr style={{ backgroundColor: '#0024d6', color: '#ffffff' }}>
                                            <th className="p-2" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6' }}>Vacina</th>
                                            <th className="p-2 text-center" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6', width: '80px' }}>Dose</th>
                                            <th className="p-2 text-center" style={{ color: '#ffffff', fontWeight: 700, backgroundColor: '#0024d6', width: '120px' }}>Data Dose</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vacinasLidasUpload.map((v, index) => (
                                            <tr key={index}>
                                                <td className="p-2 align-middle" style={{ fontWeight: 500 }}>{v.nome_identificado || '—'}</td>
                                                <td className="p-2 text-center align-middle">{v.dose ? `${v.dose}º` : '1º'}</td>
                                                <td className="p-2 text-center align-middle">{formatarDataPtBr(v.data_aplicacao)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </div>

                        <div className="d-flex justify-content-end mt-3 pt-2" style={{ borderTop: '1px solid #e2e8f0' }}>
                            <Button color="primary" onClick={() => setModalVacinasLidasAberta(false)}>
                                Entendido
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {documentoParaRecusar && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.5)', zIndex: 1060 }}
                >
                    <div style={{ background: '#fff', borderRadius: '.5rem', padding: '1.5rem', maxWidth: 480, width: '90%' }}>
                        <h5 className="mb-3"><b>Recusar documento</b></h5>
                        <p className="text-muted" style={{ fontSize: '.9rem' }}>
                            <b>{documentoParaRecusar.nome}</b> será marcado como recusado. {dados.aluno?.nome} será avisado pelo app e por e-mail e poderá enviar outro arquivo.
                        </p>

                        <FormGroup>
                            <Label><b>Motivo da recusa</b></Label>
                            <Input
                                type="select"
                                value={motivoRecusaDocumento}
                                onChange={(e) => setMotivoRecusaDocumento(e.target.value)}
                            >
                                <option value="">Selecione...</option>
                                {OPCOES_MOTIVO_RECUSA_MANUAL.map((opcao) => (
                                    <option key={opcao.valor} value={opcao.valor}>{opcao.rotulo}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label><b>Detalhes para o aluno (opcional)</b></Label>
                            <Input
                                type="textarea"
                                rows={3}
                                value={detalhesRecusaDocumento}
                                onChange={(e) => setDetalhesRecusaDocumento(e.target.value)}
                                placeholder="Ex.: A foto está desfocada e não é possível ler as datas das doses."
                            />
                        </FormGroup>

                        {erroRecusaDocumento && (
                            <div className="mb-3" style={{ color: '#dc3545', fontSize: '.85rem' }}>{erroRecusaDocumento}</div>
                        )}

                        <div className="d-flex justify-content-end">
                            <Button
                                color="primary"
                                outline
                                className="mr-2"
                                onClick={() => setDocumentoParaRecusar(null)}
                                disabled={processandoDocumentoId === documentoParaRecusar.id}
                            >
                                Cancelar
                            </Button>
                            <Button
                                color="primary"
                                onClick={confirmarRecusarDocumento}
                                disabled={processandoDocumentoId === documentoParaRecusar.id}
                            >
                                {processandoDocumentoId === documentoParaRecusar.id ? 'Recusando...' : 'Recusar documento'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {documentoParaRemover && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.5)', zIndex: 1060 }}
                >
                    <div style={{ background: '#fff', borderRadius: '.5rem', padding: '1.5rem', maxWidth: 480, width: '90%' }}>
                        <h5 className="mb-3"><b>Remover documento</b></h5>
                        <p className="text-muted" style={{ fontSize: '.9rem' }}>
                            <b>{documentoParaRemover.nome}</b> será removido definitivamente, junto com as vacinas identificadas nele.
                            Use isso só pra corrigir um envio feito por engano — não há como desfazer.
                        </p>

                        {erroRemoverDocumento && (
                            <div className="mb-3" style={{ color: '#dc3545', fontSize: '.85rem' }}>{erroRemoverDocumento}</div>
                        )}

                        <div className="d-flex justify-content-end">
                            <Button
                                color="primary"
                                outline
                                className="mr-2"
                                onClick={() => setDocumentoParaRemover(null)}
                                disabled={processandoDocumentoId === documentoParaRemover.id}
                            >
                                Cancelar
                            </Button>
                            <Button
                                color="danger"
                                onClick={confirmarRemoverDocumento}
                                disabled={processandoDocumentoId === documentoParaRemover.id}
                            >
                                {processandoDocumentoId === documentoParaRemover.id ? 'Removendo...' : 'Remover documento'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {modalEnviarDocumentoAberto && (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.5)', zIndex: 1060 }}
                >
                    <div style={{ background: '#fff', borderRadius: '.5rem', padding: '1.5rem', maxWidth: 480, width: '90%' }}>
                        <h5 className="mb-3"><b>Enviar documento</b></h5>
                        <p className="text-muted" style={{ fontSize: '.9rem' }}>
                            O documento será vinculado a <b>{dados.aluno?.nome}</b>, marcado como enviado pela Secretaria, e ficará
                            disponível no select da coluna Documento.
                        </p>

                        <FormGroup>
                            <Label><b>Tipo de documento</b></Label>
                            <Input
                                type="select"
                                value={tipoDocumentoId}
                                onChange={(e) => setTipoDocumentoId(e.target.value)}
                            >
                                <option value="">Tipo de documento...</option>
                                {(dados.tipos_documento || []).map((tipo) => (
                                    <option key={tipo.id} value={tipo.id}>{tipo.descricao}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label><b>Arquivo</b></Label>
                            <input
                                ref={inputArquivoRef}
                                type="file"
                                accept="application/pdf"
                                style={{ display: 'none' }}
                                onChange={(e) => setArquivo(e.target.files[0] || null)}
                            />
                            {arquivo ? (
                                <div
                                    className="d-flex justify-content-between align-items-center p-2"
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '.375rem' }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{arquivo.name}</div>
                                        <div className="text-muted" style={{ fontSize: '.8rem' }}>{formatarTamanhoArquivo(arquivo.size)}</div>
                                    </div>
                                    <span
                                        style={{ color: '#0024d6', fontWeight: 600, cursor: 'pointer', fontSize: '.85rem', whiteSpace: 'nowrap' }}
                                        onClick={() => inputArquivoRef.current?.click()}
                                    >
                                        Trocar arquivo
                                    </span>
                                </div>
                            ) : (
                                <Button color="primary" outline onClick={() => inputArquivoRef.current?.click()}>
                                    Escolher arquivo
                                </Button>
                            )}
                            <small className="text-muted d-block mt-1">PDF, até 10 MB.</small>
                        </FormGroup>

                        {erroUpload && <div className="mb-3" style={{ color: '#dc3545', fontSize: '.85rem' }}>{erroUpload}</div>}

                        <div className="d-flex justify-content-end">
                            <Button color="primary" outline className="mr-2" onClick={fecharModalEnviarDocumento} disabled={enviandoDocumento}>
                                Cancelar
                            </Button>
                            <Button
                                color="primary"
                                onClick={enviarDocumento}
                                disabled={!tipoDocumentoId || !arquivo || enviandoDocumento}
                            >
                                {enviandoDocumento ? 'Enviando...' : 'Enviar'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Col>
    );
}
